<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { isActive } from "@roxi/routify";
    import { ServiceConsts } from "$/services/ServiceConsts";

    import { Kindle } from "$/utils/Kindle";
    import { getRootDocument, isStringUUID } from "$/utils/Utils";

    import { SelectedSnippetsStore } from "$/stores/SelectedSnippetsStore";
    import { ActiveTopicStore } from "$/stores/extension/ActiveTopicStore";
    import { AppStore, AppType } from "$/stores/AppStore";

    import Dropdown from "$/components/Dropdown.svelte";
    import CardSelector from "$/components/card/CardSelector.svelte";
    import EditLinkDropdown from "$/components/dropdowns/EditLinkDropdown.svelte";
    import { tooltip } from "$/utils/tooltip";

    export let card;
    export let editRole;
    export let dropdownItems = null;
    export let isGridCard = false;
    export let editLink = false;

    export let enableSelecting = true;
    export let enableClosing = false;
    const dispatch = createEventDispatcher();

    let unverifiedMessage =
        "The link has been edited. <br> Try locating the snippet to <br>  &nbsp;&nbsp; remove the warning.";
    let pageUrl = null;

    $: {
        if (card.url) {
            // Set page URL
            pageUrl = card.url;
            const pdfPathPart = pageUrl.split("/pdf/")[1];
            // If the URL contains "/pdf/", we need to extract the part after it and cut trailing slashes if any.
            const pdfPath = pdfPathPart
                ? pdfPathPart.split("#")[0].replace(/\/$/, "")
                : "";
            // If page URL is dashboard URL and if the part after "/pdf/" is not a UUID,
            // that means its a external PDF link
            // and we need to decode it, otherwise we can just use the full URL
            if (
                ServiceConsts.isDashboardUrl(pageUrl) &&
                pageUrl.includes("pdf") &&
                !isStringUUID(pdfPath)
            ) {
                // Remove dashboard URL and PDF path
                pageUrl = pdfPathPart || pageUrl;
            }

            // if page url is kindle set url to https://read.amazon.com/notebook?
            else if (
                pageUrl.includes("kindle://book/") ||
                pageUrl.includes("kindle://book?action=open")
            ) {
                pageUrl = Kindle.createKindleUrl(pageUrl);
            }

            // Decode the PDF URL for readability
            pageUrl = decodeURIComponent(pageUrl);

            // split on #
            pageUrl = pageUrl.split("#")[0];

            // remove http(s)://
            if (pageUrl.includes("://")) {
                pageUrl = pageUrl.split("://")[1];
            }

            // remove trailing slash
            if (pageUrl.endsWith("/")) {
                pageUrl = pageUrl.substring(0, pageUrl.length - 1);
            }
        } else {
            pageUrl = null;
        }
    }

    // start of drag and drop functionality
    let dragImage;

    function handleDragStart(event) {
        // if theres selected snippets or shift is pressed, prevent drag
        if ($SelectedSnippetsStore.length > 0) {
            event.preventDefault();
            return;
        }

        // Set data as HTML
        if (card.name)
            event.dataTransfer.setData(
                "text/html",
                "<br><h3>" + card.name + "</h3>" + card.content + "<br>",
            );
        else
            event.dataTransfer.setData(
                "text/html",
                "<br>" + card.content + "<br>",
            );

        // If we are dragging from Notion, listen for the mouseup event
        if (document.location.href.includes("notion.so")) {
            event.stopPropagation();
            handleEndOnNotion(card.content);
        }

        $ActiveTopicStore.draggingSnippetId = card.id;
        $AppStore.snippetIsDragging = true;
    }

    async function handleDragEnd(event) {
        event.preventDefault();
        $ActiveTopicStore.draggingSnippetId = null;
        $AppStore.snippetIsDragging = false;
    }

    function handleEndOnNotion(textToPaste) {
        getRootDocument("web").addEventListener("mouseup", handleMouseUp);
        getRootDocument("web").addEventListener("mousemove", handleMouseMove);
    }

    function handleMouseUp(event) {
        if (navigator.permissions && navigator.clipboard) {
            console.log("has permissions and clipboard");

            navigator.permissions
                .query({ name: "clipboard-write" })
                .then((result) => {
                    if (
                        result.state === "granted" ||
                        result.state === "prompt"
                    ) {
                        let content;

                        if (card.name)
                            content =
                                "<h3>" + card.name + "</h3> - " + card.content;
                        else content = card.content;

                        // Clipboard permissions already granted or prompt the user for permission
                        navigator.clipboard.write([
                            new ClipboardItem({
                                "text/html": new Blob([content], {
                                    type: "text/html",
                                }),
                                "text/plain": new Blob([card.content], {
                                    type: "text/plain",
                                }),
                            }),
                        ]);
                    } else {
                        // Clipboard permissions denied
                        console.log("Clipboard access denied by the user.");
                    }
                })
                .catch((err) => {
                    console.error(
                        "Error requesting clipboard permission:",
                        err,
                    );
                });
        } else {
            // Clipboard API not supported
            console.log("Clipboard API is not supported in this browser.");
        }

        // reset store variables
        $AppStore.snippetIsDragging = false;

        // create small element to tell user that he needs to use ctrl+v to paste
        // and set that element to where the mouse is
        let isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
        let pastePrompt = document.createElement("div");
        pastePrompt.innerHTML = `Press ${isMac ? "⌘" : "Ctrl"} + V to confirm`;
        pastePrompt.style.position = "absolute";
        pastePrompt.style.zIndex = "9999";
        pastePrompt.style.top = `${event.clientY}px`;
        pastePrompt.style.left = `${event.clientX}px`;
        pastePrompt.style.maxWidth = "300px";
        pastePrompt.style.fontSize = "16px";
        pastePrompt.style.backgroundColor = "white";
        pastePrompt.style.padding = "5px";

        // remove dragImage element
        dragImage.remove();

        // append before body element
        document.body.insertBefore(pastePrompt, document.body.firstChild);

        // Remove pastePrompt element
        setTimeout(() => {
            pastePrompt.remove();
        }, 3000);

        // remove mouseup event listener
        getRootDocument("web").removeEventListener("mouseup", handleMouseUp);

        // on mouseup, remove the element
        getRootDocument("web").addEventListener("keyup", () => {
            pastePrompt.remove();
        });

        // remove mousemove event listener
        getRootDocument("web").removeEventListener(
            "mousemove",
            handleMouseMove,
        );
    }

    function handleMouseMove(event) {
        if ($AppStore.snippetIsDragging) {
            // Update dragImage position based on mouse coordinates
            dragImage.style.left = `${event.clientX}px`;
            dragImage.style.top = `${event.clientY}px`;
        }
    }

    // if snippet is not dragging anymore, remove set scrolls to 0px
    $: {
        if (!$AppStore.snippetIsDragging) {
            let scrollBars = document.getElementsByClassName(
                "cw-invisible-scroll-bar",
            );
            for (let i = 0; i < scrollBars.length; i++) {
                scrollBars[i].style.width = "0px";
                scrollBars[i].style.height = "0px";
            }
        } else if ($AppStore.snippetIsDragging) {
            // FIXME: This doesn't make any sense to be here. Store is accessible from any component
            //        this literally manipulates component that is not present in this file

            // find cw-invisible-scroll-bar's
            let scrollBars = document.getElementsByClassName(
                "cw-invisible-scroll-bar",
            );
            // we must await 100ms to make sure
            // the dragImage has been rendered and dragging has started
            setTimeout(() => {
                if (scrollBars.length > 0) {
                    // add width to scrollbars if we are not in extension
                    if (
                        $AppStore.type !== AppType.EXTENSION &&
                        $isActive("/pdf") === false
                    ) {
                        // FIXME: Extension and mobile shouldn't be so different

                        // if we are in mobile
                        if (window.innerWidth < 768 || isGridCard)
                            for (let i = 0; i < scrollBars.length; i++) {
                                scrollBars[i].style.width = "100%";
                                scrollBars[i].style.height = "10px";
                            }
                        else
                            for (let i = 0; i < scrollBars.length; i++) {
                                scrollBars[i].style.width = "10px";
                                scrollBars[i].style.height = "100%";
                            }
                    }
                    // add height to scrollbars if we are in extension
                    else {
                        for (let i = 0; i < scrollBars.length; i++) {
                            scrollBars[i].style.width = "100%";
                            scrollBars[i].style.height = "10px";
                        }
                    }
                }
            }, 100);
        }
    }
    // end of drag and drop functionality
</script>

{#if pageUrl}
    <div
        class="card-header"
        draggable="true"
        on:dragstart={handleDragStart}
        on:dragend={handleDragEnd}
    >
        {#if editRole >= 3 && enableSelecting && card.id !== null}
            <CardSelector {card} />
        {/if}
        {#if card.bookmark}
            <div class="card-header__bookmark">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="h-4 w-4"
                >
                    <path
                        fill-rule="evenodd"
                        d="M6.32 2.577a49.255 49.255 0 0 1 11.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 0 1-1.085.67L12 18.089l-7.165 3.583A.75.75 0 0 1 3.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93Z"
                        clip-rule="evenodd"
                    />
                </svg>
            </div>
        {/if}
        {#if !card.verified}
            <div class="unverified-link">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                    use:tooltip={unverifiedMessage}
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                    />
                </svg>
            </div>
        {/if}
        <a
            class="card-header__url"
            href={"http://" + pageUrl}
            target="_blank"
            rel="noreferrer"
            on:touchstart|preventDefault
            on:touchmove|preventDefault
        >
            {pageUrl}
        </a>
        {#if (editRole >= 3 && dropdownItems && card.id !== null) || card.archived}
            <Dropdown items={dropdownItems} />
        {/if}

        {#if card.id === null}
            <span class="loading-spinner">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="h-3 w-3"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                </svg>
            </span>
        {/if}
        {#if enableClosing}
            <button
                class="card-header__close"
                on:click={() => dispatch("close")}
            >
                <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                    ></path>
                </svg>
            </button>
        {/if}
    </div>
    <EditLinkDropdown
        bind:editLink
        pageUrl={decodeURIComponent(pageUrl)}
        {card}
    />
{/if}

<style>
    @import "../../../main.css";

    .card-header {
        @apply reset z-10 flex h-6 flex-shrink-0 items-center justify-between gap-1 rounded-t-xl border-l border-r border-t border-solid border-gray-200 bg-gray-100 px-4 !important;
    }

    .card-header__url {
        @apply reset m-0 block flex-1 touch-none items-center truncate bg-transparent text-xs text-gray-400 no-underline outline-none hover:text-gray-600 !important;
    }

    .card-header__close svg {
        @apply h-4 w-4 cursor-pointer text-gray-400 hover:text-black !important;
    }

    .card-header__bookmark {
        @apply reset flex h-4 w-4 items-center justify-center py-2 text-gray-300 !important;
    }

    .loading-spinner {
        @apply animate-spin text-gray-500 !important;
    }

    .unverified-link svg {
        @apply mr-auto h-4 w-4 text-gray-400 !important;
    }
</style>
